<template>
  <div class="textarea">
    <textarea
      :class="{ uppercase, filled, error: isInvalid, noOutline }"
      :id="uid"
      v-model="ivalue"
      :disabled="readOnly"
      @blur="validate" @keyup="handleKeys"
    ></textarea>
    <label v-if="placeholder" :for="uid" class="placeholder">
      {{ placeholder }}
      <template v-if="required">*</template>
    </label>
    <span v-if="error" >{{ error }}</span>
    <IconCheckmark v-if="isValid" class="valid" />
    <IconTimes v-if="isInvalid" class="error" />
  </div>
</template>

<script>
  import IconTimes from '@/assets/svg/icon-times.svg?inline';
  import IconCheckmark from '@/assets/svg/icon-checkmark.svg?inline';

  let iid = 0;

  export default {
    name: 'TextArea',
    components: {
      IconTimes,
      IconCheckmark,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      placeholder: {
        type: String,
        default: null,
      },
      validator: {
        type: [RegExp, Function],
        default: null,
      },
      required: {
        type: Boolean,
        default: false,
      },
      uppercase: {
        type: Boolean,
        default: false,
      },
      noOutline: {
        type: Boolean,
        default: false,
      },
      value: {
        type: String,
        default: null,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
      noTracking: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      iid += 1;
      return {
        uid: `textarea-${iid}`,
        ivalue: this.value || null,
        valid: null,
      };
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value(value) {
        this.ivalue = value;
      },
      ivalue(value) {
        this.$emit('update:value', this.uppercase ? String(value).toUpperCase() : value);
        this.$emit('input', this.uppercase ? String(value).toUpperCase() : value);
      },
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      filled() {
        return this.ivalue && /\S/.test(this.ivalue);
      },
      isValid() {
        const valid = this.valid;
        if (valid === null) {
          return null;
        }
        return valid === true;
      },
      isInvalid() {
        const valid = this.valid;
        if (valid === null) {
          return null;
        }
        return valid === false;
      },
      error() {
        if (this.valid !== null) {
          if (this.ivalue === null || this.ivalue === '') {
            return this.$t('registration.form.errors.empty');
          }
          if (this.isInvalid) {
            return this.$t('registration.form.errors.invalid', [this.placeholder]);
          }
        }
        return null;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      validate() {
        const validator = this.validator;
        const ref = this.$vnode.data.ref;
        const noTracking = this.noTracking;

        if (validator && this.ivalue) {
          if (validator.constructor === RegExp) {
            this.valid = validator.test(this.ivalue);
            if (!noTracking) {
              this.$b.analytics.trackFormField(ref, this.valid);
            }
            return this.valid;
          }
          if (validator.constructor === Function) {
            this.valid = validator(this.ivalue);
            if (!noTracking) {
              this.$b.analytics.trackFormField(ref, this.valid);
            }
            return this.valid;
          }
        }
        if (validator) {
          this.valid = false;
          if (!noTracking) {
            this.$b.analytics.trackFormField(ref, this.valid);
          }
          return false;
        }
        return null;
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleKeys() {
        if (this.valid !== null) {
          this.validate();
        }
      },
    },
  };
</script>

<style lang="scss">
  @import '~scss/variables';
  @import '~scss/mixins';

  .textarea {
    position: relative;
    display: inline-block;
    width: 100%;

    & > textarea {
      position: relative;
      width: 100%;
      border: 1px solid $color-gray;
      border-radius: 3px;
      background: transparent;
      outline: 0;
      padding: 25px 50px 9px 16px;
      margin: 0;
      min-height: 140px;
      color: $color-white;
      @include font-daimler-cs(400);
      font-size: 1.9rem;
      transition: border-color $default-transition;

      &:disabled {
        padding: 25px 50px 9px 0;

        & + label {
          left: 0;
          transform: translate(0, -90%) scale(0.7368);
        }
      }

      & + label {
        position: absolute;
        top: 28px;
        left: 16px;
        @include font-daimler-cs(400);
        color: $color-gray;
        transform: translate(0, -50%) scale(1);
        transform-origin: 0 0;
        transition: transform $default-transition;
        font-size: 1.9rem;
        pointer-events: none;
      }

      &:focus {
        border-color: $color-blue;
        color: $color-blue;
        & + label {
          transform: translate(0, -90%) scale(0.7368);
        }
      }

      &.filled {
        color: $color-white;
        & + label {
          transform: translate(0, -90%) scale(0.7368);
        }
      }

      &.uppercase {
        text-transform: uppercase;
      }

      &.error {
        border-color: $color-error;
      }

      &.noOutline {
        border-color: transparent;
      }
    }

    & > svg {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translate(0, -50%);
      &.error {
        path {
          fill: $color-error;
        }
        width: 15px;
        height: 14.5px;
      }
      &.valid {
        path {
          fill: $color-blue;
        }
        width: 15px;
        height: 13px;
      }
    }

    & > span {
      position: absolute;
      bottom: -1.4rem;
      font-size: 1.1rem;
      width: 100%;
      left: 0;
      padding: 0 0 0 16px;
      color: $color-error;
    }
  }
</style>
